import type { IndustrialCaseSummary } from '@/domain/industrialCase/industrialCaseSummary';
import React from 'react';

import useCurrentLang from '@/hooks/apps/useCurrentLang';

import SubtopSolution from '@/components/apps/ui/subtop/SubtopSolution';
import TransCustom from '@/components/apps/ui/transCustom/TransCustom';

import SolutionArchitectureSection from '../shared/architecture/SolutionArchitectureSection';
import CasesSection from './section/CasesSection';
import CompositeEventSection from './section/CompositeEventSection';
import FeaturesSection from './section/FeaturesSection';
import SafetyFaqSection from './section/SafetyFaqSection';
import SafetyInnovationSection from './section/SafetyInnovationSection';
import SafetyInquiryCtaSection from './section/SafetyInquiryCtaSection';
import SoundSection from './section/SoundSection';
import SystemSection from './section/SystemSection';

type Props = {
  /** Published Safety cases for this language, or empty while none is public */
  safetyCases: ReadonlyArray<IndustrialCaseSummary>;
};

export default function Safety({ safetyCases }: Props) {
  const lang = useCurrentLang();

  const desktopImage =
    lang === 'en'
      ? {
          src: '/images/apps/solution/safety/SubtopSolution_img_01_en.webp',
          width: 1480,
          height: 650,
        }
      : {
          src: '/images/apps/solution/safety/SubtopSolution_img_01.webp',
          width: 1480,
          height: 832,
        };
  const mobileImage =
    lang === 'en'
      ? {
          src: '/images/apps/solution/safety/SubtopSolution_img_01_en_m.webp',
          width: 1006,
          height: 442,
        }
      : {
          src: '/images/apps/solution/safety/SubtopSolution_img_01_m.webp',
          width: 1006,
          height: 565,
        };

  return (
    <div className="bg-apps-brandGrayBlack pb-[100px] text-apps-coreGrayWhite lg:pb-[clamp(100px,16.667vw,200px)]">
      {/*
       * 앞쪽 섹션들은 gap 으로 간격을 잡고, FAQ·도입 안내·CTA 는 자체 여백을 갖는다.
       * 자체 여백을 가진 섹션을 같은 flex 컨테이너에 두면 gap 과 겹치므로 밖으로 뺀다.
       */}
      <div className="flex flex-col gap-[100px] lg:gap-[clamp(100px,16.667vw,200px)]">
        <SubtopSolution
          kind="safety"
          subtitle={
            <TransCustom i18nKey="보이지 않은 위험을 소리로 가장 먼저 파악하는 AI" />
          }
          subtitleClassName="text-apps-corePrimary01"
          title={
            <TransCustom i18nKey="응급 상황을 인식하는 Listen AI 안전 솔루션" />
          }
          desktopImage={desktopImage}
          mobileImage={mobileImage}
          imageAlt="Listen AI 안전 솔루션"
        />

        {/* 핵심 기능 3 + TTA 인증 지표 */}
        <FeaturesSection />

        {/* 복합 이벤트 */}
        <CompositeEventSection />

        {/* 감지 가능한 주요 사운드 (상황별 묶음) */}
        <SoundSection />

        {/* 모니터링 시스템 */}
        <SystemSection />

        {/* 엣지 분석서버 구성 방식 */}
        <SolutionArchitectureSection variant="edge" idPrefix="safety" />

        {/* 고객 사례 */}
        <CasesSection cases={safetyCases} />

        {/* 조달청 혁신제품 지정 */}
        <SafetyInnovationSection />
      </div>

      {/* 자주 묻는 질문 */}
      <SafetyFaqSection />

      {/* 도입 상담 문의 */}
      <SafetyInquiryCtaSection />
    </div>
  );
}
